import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

interface AlertItem {
  id: number;
  store_id: number;
  store_name: string;
  alert_type: string;
  severity: string;
  title: string;
  status: string;
  created_at: string;
  attachment_count: number;
}

interface Store {
  id: number;
  name: string;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  theft_shoplifting: 'Theft / Shoplifting',
  robbery: 'Robbery',
  safety_hazard: 'Safety Hazard',
  employee_issue: 'Employee Issue',
  suspicious_activity: 'Suspicious Activity',
  customer_incident: 'Customer Incident',
  maintenance: 'Maintenance',
  delivery: 'Delivery',
  cash_handling: 'Cash Handling',
  shift_change: 'Shift Change',
  fuel_incident: 'Fuel Incident',
  custom: 'Custom',
  theft: 'Theft',
  employee_theft: 'Employee Theft',
  no_show: 'No Show',
  late_arrival: 'Late Arrival',
  early_departure: 'Early Departure',
  unauthorized_area: 'Unauthorized Area',
  safety_violation: 'Safety Violation',
  property_damage: 'Property Damage',
  maintenance_issue: 'Maintenance Issue',
  inventory: 'Inventory',
  other: 'Other',
};

const SEVERITY_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  low: { label: 'Low', badgeClass: 'badge badge-neutral' },
  medium: { label: 'Medium', badgeClass: 'badge badge-info' },
  high: { label: 'High', badgeClass: 'badge badge-warning' },
  critical: { label: 'Critical', badgeClass: 'badge badge-critical' },
};

const STATUS_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  open: { label: 'Open', badgeClass: 'badge badge-info' },
  acknowledged: { label: 'Acknowledged', badgeClass: 'badge badge-warning' },
  resolved: { label: 'Resolved', badgeClass: 'badge badge-success' },
  closed: { label: 'Closed', badgeClass: 'badge badge-neutral' },
};

function formatEventType(eventType: string): string {
  return EVENT_TYPE_LABELS[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

const PAGE_SIZE = 20;

export default function AlertListPage() {
  const navigate = useNavigate();

  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const [stores, setStores] = useState<Store[]>([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStore, setFilterStore] = useState('');

  // Load stores for filter dropdown
  useEffect(() => {
    api.get('/monitors/stores').then(res => {
      setStores(res.data.stores || []);
    }).catch(() => {});
  }, []);

  const fetchAlerts = useCallback(async (offset: number, append: boolean) => {
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      if (filterStatus) params.set('status', filterStatus);
      if (filterSeverity) params.set('severity', filterSeverity);
      if (filterStore) params.set('store_id', filterStore);

      const res = await api.get(`/monitors/alerts?${params.toString()}`);
      if (append) {
        setAlerts(prev => [...prev, ...res.data.alerts]);
      } else {
        setAlerts(res.data.alerts);
      }
      setTotal(res.data.total);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load alerts');
    }
  }, [filterStatus, filterSeverity, filterStore]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    (async () => {
      await fetchAlerts(0, false);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchAlerts]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await fetchAlerts(alerts.length, true);
    setLoadingMore(false);
  };

  const hasMore = alerts.length < total;

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <h2 style={styles.pageTitle}>Alerts</h2>

        {/* Filter bar */}
        <div style={styles.filterBar}>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filterSeverity}
            onChange={e => setFilterSeverity(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={filterStore}
            onChange={e => setFilterStore(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="">All Stores</option>
            {stores.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>

        {loading && <p style={styles.statusText}>Loading alerts...</p>}
        {error && <div style={styles.errorBanner}>{error}</div>}

        {!loading && !error && alerts.length === 0 && (
          <p style={styles.statusText}>No alerts found.</p>
        )}

        {!loading && alerts.length > 0 && (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date / Time</th>
                    <th style={styles.th}>Store</th>
                    <th style={styles.th}>Type</th>
                    <th style={styles.th}>Severity</th>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Status</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map(alert => {
                    const sevConfig = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.low;
                    const statConfig = STATUS_CONFIG[alert.status] || STATUS_CONFIG.open;
                    return (
                      <tr
                        key={alert.id}
                        style={styles.tr}
                        onClick={() => navigate(`/alerts/${alert.id}`)}
                        onMouseEnter={e => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--table-row-hover)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLTableRowElement).style.background = ''; }}
                      >
                        <td style={styles.td}>
                          <span style={styles.timestamp}>{formatTimestamp(alert.created_at)}</span>
                        </td>
                        <td style={styles.td}>{alert.store_name}</td>
                        <td style={styles.td}>{formatEventType(alert.alert_type)}</td>
                        <td style={styles.td}>
                          <span className={sevConfig.badgeClass}>
                            {sevConfig.label}
                          </span>
                        </td>
                        <td style={{ ...styles.td, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {alert.title}
                        </td>
                        <td style={styles.td}>
                          <span className={statConfig.badgeClass}>
                            {statConfig.label}
                          </span>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={e => { e.stopPropagation(); navigate(`/alerts/${alert.id}`); }}
                            style={styles.viewBtn}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div style={styles.loadMoreWrap}>
                <button onClick={handleLoadMore} disabled={loadingMore} style={{
                  ...styles.loadMoreBtn,
                  opacity: loadingMore ? 0.5 : 1,
                  cursor: loadingMore ? 'not-allowed' : 'pointer',
                }}>
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {},
  main: {
    maxWidth: 1060,
    margin: '0 auto',
    padding: '32px 24px',
  },
  pageTitle: {
    fontFamily: 'var(--font-display)',
    fontSize: 'var(--text-2xl)',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: '0 0 20px',
  },
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
  },
  filterSelect: {
    padding: '8px 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'var(--input-bg)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
    minWidth: 140,
  },
  statusText: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)',
    color: 'var(--status-critical)',
    padding: '14px 18px',
    borderRadius: 'var(--radius-lg)',
    fontSize: 'var(--text-md)',
    fontFamily: 'var(--font-body)',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--border-subtle)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: 'var(--text-sm)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 14px',
    fontSize: 'var(--text-xs)',
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    background: 'var(--table-header-bg)',
    borderBottom: '1px solid var(--border-subtle)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  td: {
    padding: '12px 14px',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-body)',
    verticalAlign: 'middle' as const,
    whiteSpace: 'nowrap' as const,
  },
  timestamp: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--text-xs)',
    color: 'var(--text-secondary)',
  },
  viewBtn: {
    padding: '4px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--accent)',
    background: 'transparent',
    color: 'var(--accent-text)',
    fontSize: 'var(--text-xs)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    cursor: 'pointer',
  },
  loadMoreWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 20,
  },
  loadMoreBtn: {
    padding: '8px 28px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-default)',
    background: 'transparent',
    fontSize: 'var(--text-sm)',
    fontWeight: 500,
    fontFamily: 'var(--font-body)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
  },
};
